import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import { useUsersByEmail } from '@/services/user'
import { useAuthStore } from '@/store/authStore'
import { useSessionStore } from '@/store/sessionStore'
import { userHasPermission } from '@/utils/permissions'

/**
 * useBootstrapUser.ts
 * React port of Blazor LandingPage.razor → CheckAuthorization().
 *
 * Called once from ProtectedRoute after MSAL authentication completes.
 * Flow (mirrors Blazor exactly):
 *   1. Extract email from MSAL account (was: Claims lookup by TokenEmailkey)
 *   2. Call GET Users/ByEmail/{email} (was: UsersViewModel.GetUserByEmail)
 *   3. If user is null or inactive → store NoRoleAssigned → redirect /access_denied
 *   4. Resolve tenant: if both DUWL+DUIN → default to DUWL (Blazor rule)
 *   5. Convert comma-separated RoleIds → role name strings (was: GetRolesByCommaSeparatedRoleIds)
 *   6. Populate authStore (roles, tenant, displayName) + sessionStore (email, alias, tenant)
 *   7. Redirect:
 *        CapacityApplication + DUWL → /dashboard
 *        AttendanceTrackingApplication → /attendancetracking
 *        else → /access_denied
 *
 * Returns { isBootstrapping } — true while the API call is in flight.
 */

/** Mirrors Blazor UserRole enum: numeric ID → string name */
const ROLE_ID_MAP: Record<number, string> = {
  0: 'NoRoleAssigned',
  1: 'ApplicationAdmin',
  2: 'AdminCapacityManagement',
  3: 'ViewerCapacityManagement',
  4: 'AdminAttendanceTracking',
  5: 'ViewerAttendanceTracking',
  6: 'UserCapacityManagement',
  7: 'UserAttendanceTracking',
}

function resolveRoles(roleIds: string | null | undefined): string[] {
  if (!roleIds) return ['NoRoleAssigned']
  return roleIds
    .split(',')
    .map((id) => ROLE_ID_MAP[parseInt(id.trim(), 10)] ?? 'NoRoleAssigned')
    .filter(Boolean)
}

const MOCK_EMAIL   = 'psaareddy@deloitte.com'
const DISABLE_AUTH = import.meta.env.VITE_DISABLE_AUTH === 'true'

export function useBootstrapUser() {
  // Always call useMsal() unconditionally.
  // In DISABLE_AUTH mode, MockAuthProvider ensures it's safe.
  // In production mode, MsalProvider ensures it's safe.
  const msalContext  = useMsal()
  const navigate     = useNavigate()
  const setUser      = useAuthStore((s) => s.setUser)
  const isBootstrapped = useAuthStore((s) => s.isAuthenticated)
  const setSessionKey = useSessionStore((s) => s.set)

  // Email: from MSAL account in production, or MOCK_EMAIL when auth is disabled (API key auth)
  const email = DISABLE_AUTH ? MOCK_EMAIL : (msalContext.accounts[0]?.username ?? '')

  // Display name from MSAL token (production). In localhost mode it is derived from the
  // API response (firstName + lastName) inside the useEffect below, where user data is available.
  const msalDisplayName = msalContext?.accounts[0]?.name ?? ''

  // Always call the real API — VITE_API_KEY is used as the auth header when DISABLE_AUTH=true
  const { data: userRecords, isLoading: loading, isError: hasError } = useUsersByEmail(email)

  useEffect(() => {
    // Skip if already bootstrapped this session or still loading
    if (isBootstrapped || loading || !email) return

    // Filter to active records only
    const activeUsers = (userRecords ?? []).filter((u) => u.isActive)
    const isUnauthorized = activeUsers.length === 0

    if (hasError || isUnauthorized) {
      // Mirror: _sessionStorage.SetItem(UserRoleID, UserRole.NoRoleAssigned) + NavigateTo(access_denied)
      setUser({
        email,
        alias:            email.split('@')[0] ?? '',
        displayName:      msalDisplayName || email,
        roles:            ['NoRoleAssigned'],
        tenant:           '',
        availableTenants: [],
        tenantRoles:      {},
      })
      navigate('/access_denied', { replace: true })
      return
    }

    // Build per-tenant role map — one record per tenant
    const tenantRoles: Record<string, string[]> = {}
    for (const u of activeUsers) {
      const t = u.tenant?.trim()
      if (t) {
        tenantRoles[t] = resolveRoles(u.roleIds)
      }
    }

    // Available tenants from all active records
    const availableTenants = Object.keys(tenantRoles)
    if (availableTenants.length === 0) availableTenants.push('DUWL')

    // Default to DUWL if available (mirrors Blazor rule)
    const tenant = availableTenants.includes('DUWL') ? 'DUWL' : (availableTenants[0] ?? 'DUWL')

    // Roles for the initial/primary tenant
    const roles = tenantRoles[tenant] ?? ['NoRoleAssigned']

    // Primary record for display name
    const primaryRecord = activeUsers.find((u) => u.tenant?.trim() === tenant) ?? activeUsers[0]
    const alias = email.split('@')[0] ?? ''
    const displayName = DISABLE_AUTH
      ? (`${primaryRecord?.firstName ?? ''} ${primaryRecord?.lastName ?? ''}`).trim() || alias
      : msalDisplayName || alias

    // Populate authStore
    setUser({ email, alias, displayName, roles, tenant, availableTenants, tenantRoles })

    // Populate sessionStore
    setSessionKey('loggedInUserEmail', email)
    setSessionKey('loggedInUserName', displayName)
    setSessionKey('alias', alias)
    setSessionKey('tenant', tenant)

    // Redirect — mirrors Blazor navigation logic exactly
    const isDuwl = tenant === 'DUWL'
    if (userHasPermission(roles, 'CapacityApplication') && isDuwl) {
      navigate('/dashboard', { replace: true })
    } else if (userHasPermission(roles, 'AttendanceTrackingApplication')) {
      navigate('/attendancetracking', { replace: true })
    } else {
      navigate('/access_denied', { replace: true })
    }
  }, [email, userRecords, loading, hasError, isBootstrapped, navigate, setUser, setSessionKey, msalDisplayName])

  return { isBootstrapping: loading && !!email && !isBootstrapped }
}
